import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import { useAuth } from "../lib/auth.jsx";
import { IconSearch, IconPlay, IconShield } from "../components/icons.jsx";

function relativeTime(iso, t) {
  if (!iso) return t("common_never");
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

export default function DevicesPage() {
  const { t } = useT();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [twoFa, setTwoFa] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [planRequired, setPlanRequired] = useState(null);

  async function load() {
    try {
      const data = await api("/devices");
      setDevices(data.devices);
    } catch (err) {
      // Don't toast every poll error — only first time
      if (!devices) {
        toast.error("Couldn't load devices: " + err.message);
      }
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    if (!devices) return null;
    const f = filter.trim().toLowerCase();
    if (!f) return devices;
    return devices.filter((d) =>
      d.device_name?.toLowerCase().includes(f) ||
      d.hostname?.toLowerCase().includes(f) ||
      d.device_id?.toLowerCase().includes(f) ||
      d.os?.toLowerCase().includes(f)
    );
  }, [devices, filter]);

  const stats = useMemo(() => {
    if (!devices) return null;
    return {
      total: devices.length,
      online: devices.filter((d) => d.online).length,
      linux: devices.filter((d) => d.platform === "linux").length,
      windows: devices.filter((d) => d.platform === "windows").length,
    };
  }, [devices]);

  async function toggleBrowserAccess(device, enabled) {
    try {
      await api(`/browser/devices/${device.id}/toggle`, {
        method: "POST",
        body: { enabled },
      });
      await load();
      toast.success(
        enabled
          ? `Browser-Access für ${device.device_name} aktiviert`
          : `Browser-Access für ${device.device_name} deaktiviert`
      );
    } catch (err) {
      toast.error("Toggle fehlgeschlagen: " + err.message);
    }
  }

  async function startConnect(device) {
    setError(null);
    try {
      const r = await api("/browser/connect/start", {
        method: "POST",
        body: { device_id: device.device_id },
      });
      if (r.requires_2fa) {
        setTwoFa({
          device,
          code_prefix: r.code_prefix,
          expires_at: r.expires_at,
        });
      } else {
        // No 2FA required — jump straight to viewer
        navigate(`/connect/${encodeURIComponent(r.session_token)}/${device.device_id}`);
      }
    } catch (err) {
      // Plan required → show upgrade modal instead of plain error
      if (err.status === 402 && err.details?.error === "active_plan_required") {
        setPlanRequired({
          device,
          currentPlan: err.details.current_plan,
          planActive: err.details.plan_active,
        });
        return;
      }
      // Maintenance message
      if (err.details?.error === "browser_access_disabled") {
        toast.warn(err.details.message || "Browser-Access is temporarily disabled", {
          title: "Maintenance",
        });
        return;
      }
      // Host offline
      if (err.status === 409) {
        toast.error("Host is offline. Make sure the Vorks Connect app is running on the target machine.", {
          title: "Can't connect",
        });
        return;
      }
      toast.error(err.message, { title: "Connect failed" });
    }
  }

  if (devices === null && !error) return <div className="loading">{t("common_loading")}</div>;

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">{t("devices_fleet")}</div>
          <h1 className="h1">{t("devices_title")}</h1>
        </div>
        {stats && (
          <div className="ph-meta">
            <div><strong>{stats.online}</strong> / {stats.total} {t("devices_online")}</div>
          </div>
        )}
      </div>

      {stats && (
        <div className="stats">
          <Stat label={t("devices_total")} value={stats.total} />
          <Stat label={t("devices_online")} value={stats.online} accent />
          <Stat label="Linux" value={stats.linux} />
          <Stat label="Windows" value={stats.windows} />
        </div>
      )}

      <div className="toolbar">
        <div className="search">
          <IconSearch className="search-icon" size={14} />
          <input
            type="text"
            placeholder={t("devices_search")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error-line">{error}</div>}

      {filtered && filtered.length === 0 && (
        <div className="empty">
          <div className="empty-title">{t("devices_no_devices")}</div>
          <div className="empty-body">{t("devices_no_devices_sub")}</div>
        </div>
      )}

      <div className="grid">
        <AnimatePresence>
          {filtered?.map((d, i) => (
            <motion.div
              key={d.id}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.4), duration: 0.4 }}
              className={`card ${d.online ? "online" : "offline"}`}
            >
              <div className="card-head">
                <div>
                  <div className="card-name">{d.device_name}</div>
                  <div className="card-uuid">{d.device_id}</div>
                </div>
                <span className={`chip ${d.online ? "ok" : ""}`}>
                  <span className={`dot ${d.online ? "live" : "dead"}`} />
                  {d.online ? "live" : "offline"}
                </span>
              </div>
              <div className="chips">
                {d.os && <span className="chip">{d.os}</span>}
                {d.arch && <span className="chip">{d.arch}</span>}
                {d.hostname && <span className="chip">{d.hostname}</span>}
                {d.browser_access_enabled ? (
                  <span className="chip acc"><IconShield size={10} /> browser access</span>
                ) : null}
              </div>
              <div className="meta-line">
                <span>{t("devices_last_seen")} — {relativeTime(d.last_seen_at, t)} {t("devices_ago")}</span>
                {d.owner_name && <span>{d.owner_name}</span>}
              </div>

              {/* Browser-Access Toggle */}
              <div className="setting-row" style={{ padding: "8px 0", borderTop: "1px solid var(--line)" }}>
                <div className="label-block">
                  <div className="lbl" style={{ fontSize: 11 }}>Browser-Access</div>
                  <div className="key" style={{ fontSize: 10 }}>
                    {d.browser_access_enabled ? "enabled — connect from dashboard" : "disabled — toggle on to allow"}
                  </div>
                </div>
                <div
                  className={"toggle " + (d.browser_access_enabled ? "on" : "")}
                  onClick={() => toggleBrowserAccess(d, !d.browser_access_enabled)}
                  role="switch"
                  aria-checked={!!d.browser_access_enabled}
                >
                  <div className="knob" />
                </div>
              </div>

              <div className="actions" style={{ display: "flex", gap: 6 }}>
                <button
                  className="btn primary"
                  onClick={() => startConnect(d)}
                  disabled={!d.online || (!d.browser_access_enabled && user?.role !== "superadmin")}
                  style={{ flex: 1 }}
                  title={
                    !d.online
                      ? "Host is offline"
                      : (!d.browser_access_enabled && user?.role !== "superadmin")
                        ? "Enable browser-access first"
                        : ""
                  }
                >
                  <IconPlay size={12} /> {t("devices_connect")}
                </button>
                {(d.owner_user_id === user?.id || user?.role === "admin" || user?.role === "superadmin") && (
                  <button
                    className="btn ghost danger"
                    onClick={() => setDeleteTarget(d)}
                    title="Gerät entfernen"
                    style={{ padding: "10px 14px" }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {deleteTarget && (
          <DeleteDeviceModal
            device={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirmed={(name) => {
              setDeleteTarget(null);
              toast.success(`Gerät "${name}" entfernt`);
              load();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {twoFa && (
          <TwoFaModal
            device={twoFa.device}
            codePrefix={twoFa.code_prefix}
            expiresAt={twoFa.expires_at}
            onClose={() => setTwoFa(null)}
            onSuccess={(token) => {
              setTwoFa(null);
              navigate(`/connect/${encodeURIComponent(token)}/${twoFa.device.device_id}`);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {planRequired && (
          <PlanRequiredModal
            currentPlan={planRequired.currentPlan}
            planActive={planRequired.planActive}
            onClose={() => setPlanRequired(null)}
            onUpgrade={() => {
              setPlanRequired(null);
              navigate("/billing");
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function TwoFaModal({ device, codePrefix, expiresAt, onClose, onSuccess }) {
  const { t } = useT();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
  const min = Math.floor(remaining / 60);
  const sec = remaining % 60;

  async function submit(e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code");
      return;
    }
    setBusy(true); setError(null);
    try {
      const r = await api("/browser/connect/verify", {
        method: "POST",
        body: { device_id: device.device_id, code },
      });
      onSuccess(r.session_token);
    } catch (err) {
      setError(err.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(12px)", display: "flex", alignItems: "center",
        justifyContent: "center", zIndex: 200, padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.98, opacity: 0 }}
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--acc)",
          borderRadius: "var(--r-lg)",
          padding: 36, maxWidth: 440, width: "100%",
          boxShadow: "0 0 0 1px var(--acc-glow), 0 32px 100px rgba(0,0,0,0.8)",
          position: "relative", overflow: "hidden",
        }}
      >
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, transparent, var(--acc), transparent)",
        }} />
        <div className="eyebrow">Two-factor verification</div>
        <h2 style={{ fontSize: 22, fontWeight: 600, margin: "8px 0 4px" }}>
          Code sent
        </h2>
        <p className="dim" style={{ fontSize: 13, marginTop: 0 }}>
          A 6-digit code was sent to your email and Discord (if linked). Enter
          it below to start the connection to <strong style={{ color: "var(--text)" }}>{device.device_name}</strong>.
        </p>
        <p className="mono" style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>
          Hint: code starts with <strong style={{ color: "var(--acc)" }}>{codePrefix}</strong> · expires in {min}:{String(sec).padStart(2, "0")}
        </p>

        <form onSubmit={submit}>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            autoFocus
            disabled={busy}
            placeholder="123456"
            style={{
              fontSize: 32, letterSpacing: "0.25em",
              textAlign: "center", fontFamily: "var(--mono)",
              fontWeight: 700, margin: "20px 0",
              padding: "16px 12px",
              color: "var(--acc)",
            }}
          />
          {error && <div className="error-line">{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn ghost" onClick={onClose} disabled={busy} style={{ flex: 1 }}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={busy || code.length !== 6} style={{ flex: 2 }}>
              {busy ? "Verifying…" : "Verify & connect"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className={"stat-value" + (accent ? " acc" : "")}>{value}</div>
    </div>
  );
}

function DeleteDeviceModal({ device, onClose, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api(`/devices/${device.id}`, { method: "DELETE" });
      onConfirmed(device.device_name);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, padding: 20,
      }}
      onClick={() => !busy && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--err)",
          borderRadius: "var(--r-lg)",
          padding: 32,
          maxWidth: 440, width: "100%",
          boxShadow: "0 32px 80px rgba(0,0,0,0.8)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow" style={{ color: "var(--err)" }}>Achtung</div>
        <h2 style={{ fontSize: 20, margin: "8px 0 12px" }}>Gerät entfernen?</h2>
        <p style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 18 }}>
          <strong style={{ color: "var(--text)" }}>{device.device_name}</strong> wird aus deinem Account entfernt.
          Auf dem Gerät selbst läuft die App weiter, aber sie ist nicht mehr verbunden.
        </p>
        <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 18, lineHeight: 1.6 }}>
          UUID: {device.device_id}<br />
          OS: {device.os || "—"} • Hostname: {device.hostname || "—"}
        </div>
        {error && <div className="error-line" style={{ marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button className="btn danger" onClick={confirm} disabled={busy}>
            {busy ? "Entferne…" : "Endgültig entfernen"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function PlanRequiredModal({ currentPlan, planActive, onClose, onUpgrade }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--acc)",
          borderRadius: "var(--r-lg)",
          padding: 36,
          maxWidth: 460, width: "100%",
          boxShadow: "0 0 0 1px var(--acc-glow), 0 32px 80px rgba(0,0,0,0.8)",
          position: "relative", overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, transparent, var(--acc), transparent)",
        }} />

        <div style={{
          width: 56, height: 56,
          borderRadius: "50%",
          border: "1px solid var(--acc)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 18,
          fontSize: 24,
          color: "var(--acc)",
          boxShadow: "0 0 20px var(--acc-glow)",
        }}>
          ★
        </div>

        <div className="eyebrow">Active plan required</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "8px 0 12px" }}>
          Upgrade um über den Browser zu verbinden
        </h2>
        <p style={{ color: "var(--text-dim)", fontSize: 13, lineHeight: 1.55, marginBottom: 20 }}>
          Der Vorks Connect Browser-Zugriff (Verbinden über das Dashboard) ist Teil
          der kostenpflichtigen Pläne. Mit deinem aktuellen Plan kannst du den Vorks
          Connect Desktop-Client weiterhin uneingeschränkt nutzen.
        </p>

        <div style={{
          background: "var(--bg)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r)",
          padding: "12px 14px",
          marginBottom: 22,
          fontFamily: "var(--mono)",
          fontSize: 11,
          color: "var(--text-dim)",
        }}>
          Aktueller Plan: <strong style={{ color: "var(--text)" }}>{currentPlan || "Free"}</strong>
          {!planActive && currentPlan && currentPlan !== "free" && (
            <span style={{ color: "var(--err)", marginLeft: 8 }}>(abgelaufen)</span>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn ghost" onClick={onClose}>Später</button>
          <button className="btn primary" onClick={onUpgrade}>
            Plan ansehen →
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
